import { ActionService } from '@ignitesales/neuro-application/src/neuro-guide/services/ActionService.mjs';
import { useModal } from '../core-components/ModalProvider';

/**
 * Handle action triggering with support for pop/slide UI actions.
 *
 * Pop/slide actions are opened through the central ModalProvider (DIALOGUE-2576);
 * run/systemFunction actions execute immediately. The hook no longer owns modal
 * state or renders UI — callers just use triggerAction / triggerActions.
 *
 * @param {Object} options
 * @param {Array} options.answers - Answers array threaded into the modal context
 * @param {Object} options.contextData - Additional context data for actions
 */
export const useActionTrigger = ({ answers = [], contextData = {} } = {}) => {
    const { openModal } = useModal();

    const triggerAction = async (action, trigger, additionalContext = {}) => {
        const { actionType } = action;
        const mergedContext = { ...contextData, ...additionalContext };

        if (action.targetType === 'systemFunction') {
            // System functions always execute client-side immediately
            const params = await ActionService.extractActionParams(action, mergedContext);
            await ActionService.triggerAction(action, trigger._id, params);
        } else if (actionType === 'pop' || actionType === 'slide') {
            // Centrally owned by ModalProvider; closed via the closeModal action or the X button.
            openModal(action, { trigger, answers, ...mergedContext });
        } else if (actionType === 'run') {
            // Execute run action immediately
            const params = await ActionService.extractActionParams(action, mergedContext);
            await ActionService.triggerAction(action, trigger._id, params);
        }
    };

    const triggerActions = async (actions, triggerType, filterFn = null, additionalContext = {}) => {
        if (!actions || !Array.isArray(actions)) {
            return;
        }

        for (const action of actions) {
            const triggers = await ActionService.extractAllTriggersByTriggerType(action, triggerType);

            for (const trigger of triggers) {
                // Apply filter if provided
                if (filterFn && !filterFn(trigger, action)) {
                    continue;
                }

                // Check fireOnce
                if (trigger.fireOnce && ActionService.checkIfActionFiredOnce(action, trigger)) {
                    continue;
                }

                await triggerAction(action, trigger, additionalContext);
            }
        }
    };

    return {
        triggerAction,
        triggerActions
    };
};

export default useActionTrigger;
